import { Injectable } from "@angular/core";
import { ActivatedRoute, NavigationEnd, Params, Router } from "@angular/router";
import { Breadcrumb } from "@components";
import { BehaviorSubject } from "rxjs";
import { filter } from "rxjs/operators";
import { TranslateService } from "./translate.service";

@Injectable({ providedIn: "root" })
export class BreadcrumbService {
  private _breadcrumbsSubject = new BehaviorSubject<Breadcrumb[]>([]);
  public breadcrumbs$ = this._breadcrumbsSubject.asObservable();

  constructor(
    private _router: Router,
    private _activatedRoute: ActivatedRoute,
    private _translateService: TranslateService,
  ) {
    this._router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      const breadcrumbs: Breadcrumb[] = this._createBreadcrumbs(this._activatedRoute.root);
      this._breadcrumbsSubject.next(breadcrumbs);
    });
  }

  private _createBreadcrumbs(
    route: ActivatedRoute,
    url = "",
    breadcrumbs: Breadcrumb[] = [],
  ): Breadcrumb[] {
    const children = route.children;

    if (children.length === 0) {
      return breadcrumbs;
    }

    for (const child of children) {
      const routeURL = child.snapshot.url.map((segment) => segment.path).join("/");

      if (routeURL !== "") {
        url += `/${routeURL}`;
      }

      if (child.snapshot.data && child.snapshot.data.breadcrumb) {
        const breadcrumbLabel = this._extractBreadcrumbLabel(
          child.snapshot.data.breadcrumb,
          child.snapshot.params,
        );

        breadcrumbs.push({ label: breadcrumbLabel, route: url });
      }

      return this._createBreadcrumbs(child, url, breadcrumbs);
    }

    return breadcrumbs;
  }

  private _extractBreadcrumbLabel(breadcrumbLabel: string, params: Params): string {
    const label: string = breadcrumbLabel.startsWith("app.")
      ? this._translateService.getTranslation(breadcrumbLabel)
      : breadcrumbLabel;

    return label.replace(":detail", params.detail);
  }

  public updateLastBreadcrumbLabel(label: string): void {
    const breadcrumbs: Breadcrumb[] = this._breadcrumbsSubject.value;

    if (breadcrumbs.length === 0) {
      return;
    }

    const updatedBreadcrumbs: Breadcrumb[] = [...breadcrumbs];
    updatedBreadcrumbs[updatedBreadcrumbs.length - 1] = {
      ...updatedBreadcrumbs[updatedBreadcrumbs.length - 1],
      label,
    };

    this._breadcrumbsSubject.next(updatedBreadcrumbs);
  }
}
